import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TOKEN_KEY = "ttt_admin_token";

const STATUS_LABELS = {
  new: "New",
  in_progress: "In Progress",
  fulfilled: "Fulfilled",
  cancelled: "Cancelled",
};

const STATUS_STYLES = {
  new: "bg-[#E89149] text-white",
  in_progress: "bg-[#F4C95D] text-[#2C4C3B]",
  fulfilled: "bg-[#2C4C3B] text-white",
  cancelled: "bg-[#7A1F1F] text-white",
};

function LoginForm({ onLoggedIn }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLoggedIn(data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-white border border-[#EFE7D6] rounded-2xl shadow-sm p-8"
      >
        <div className="text-[11px] tracking-[0.22em] text-[#E89149] uppercase mb-1">
          Thai Tasty Treats
        </div>
        <h1 className="font-display text-2xl text-[#2C4C3B] font-semibold mb-6">
          Admin Sign-in
        </h1>
        <label className="block text-xs uppercase tracking-wider text-[#4A5D53] mb-2">
          Password
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          autoFocus
          className="w-full px-3 py-2 border border-[#D9D2BD] rounded-md focus:outline-none focus:border-[#2C4C3B] bg-[#FBF6EA]"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-5 w-full bg-[#E89149] hover:bg-[#D17F38] text-white tracking-wider text-sm py-2.5 rounded-md transition disabled:opacity-60"
        >
          {loading ? "Signing in..." : "SIGN IN"}
        </button>
      </form>
    </div>
  );
}

function StatusBadge({ value }) {
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLES[value] || "bg-gray-200 text-gray-700"}`}
    >
      {STATUS_LABELS[value] || value}
    </span>
  );
}

function OrderRow({ order, onChangeStatus }) {
  const [open, setOpen] = useState(false);
  const dt = new Date(order.created_at);
  const dateStr = dt.toLocaleString();
  const itemsSummary = order.items
    .map((i) => `${i.name.replace("Thai Iced ", "")} ×${i.quantity}`)
    .join(", ");

  return (
    <>
      <tr
        className="border-b border-[#EFE7D6] hover:bg-[#FBF6EA] cursor-pointer"
        onClick={() => setOpen(!open)}
        data-testid={`order-row-${order.id}`}
      >
        <td className="px-4 py-3 text-xs text-[#4A5D53] font-mono">{order.id.slice(0, 8)}</td>
        <td className="px-4 py-3 text-sm text-[#3A3A3A]">{dateStr}</td>
        <td className="px-4 py-3 text-sm text-[#3A3A3A]">
          <div className="font-medium">{order.customer_name}</div>
          <div className="text-xs text-[#7A7A7A]">{order.customer_email}</div>
        </td>
        <td className="px-4 py-3 text-sm text-[#3A3A3A] max-w-xs truncate">{itemsSummary}</td>
        <td className="px-4 py-3 text-sm text-[#3A3A3A] capitalize">{order.delivery_method}</td>
        <td className="px-4 py-3 text-sm font-semibold text-[#2C4C3B]">${order.total_amount.toFixed(2)}</td>
        <td className="px-4 py-3"><StatusBadge value={order.fulfillment_status} /></td>
      </tr>
      {open && (
        <tr className="bg-[#FBF6EA]">
          <td colSpan={7} className="px-6 py-5">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#4A5D53] mb-2">Customer</div>
                <div className="text-sm">{order.customer_name}</div>
                <div className="text-sm text-[#4A5D53]">{order.customer_email}</div>
                <div className="text-sm text-[#4A5D53]">{order.customer_phone}</div>
                <div className="text-sm capitalize text-[#4A5D53] mt-1">Method: {order.delivery_method}</div>
                <div className="text-xs text-[#7A7A7A] mt-1 font-mono">Order ID: {order.id}</div>
                <div className="text-xs text-[#7A7A7A] font-mono">Square ID: {order.square_payment_id}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#4A5D53] mb-2">Items</div>
                {order.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-1">
                    <span>{i.name} × {i.quantity}</span>
                    <span>${(i.price * i.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t border-[#EFE7D6] mt-2 pt-2 text-sm">
                  <div className="flex justify-between text-[#4A5D53]"><span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-[#4A5D53]"><span>Tax</span><span>${order.tax_amount.toFixed(2)}</span></div>
                  <div className="flex justify-between font-semibold text-[#2C4C3B] mt-1"><span>Total</span><span>${order.total_amount.toFixed(2)}</span></div>
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2 items-center">
              <span className="text-[11px] uppercase tracking-wider text-[#4A5D53] mr-2">Set status:</span>
              {Object.keys(STATUS_LABELS).map((s) => (
                <button
                  key={s}
                  onClick={(e) => { e.stopPropagation(); onChangeStatus(order.id, s); }}
                  data-testid={`set-status-${s}-${order.id}`}
                  disabled={order.fulfillment_status === s}
                  className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-md border transition ${
                    order.fulfillment_status === s
                      ? "bg-[#2C4C3B] text-white border-[#2C4C3B] cursor-default"
                      : "bg-white text-[#2C4C3B] border-[#D9D2BD] hover:bg-[#F4ECD9]"
                  }`}
                >
                  {STATUS_LABELS[s]}
                </button>
              ))}
              <a
                href={`mailto:${order.customer_email}`}
                onClick={(e) => e.stopPropagation()}
                className="ml-auto text-xs text-[#E89149] hover:underline"
              >
                Email customer →
              </a>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Dashboard({ token, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders${filter ? `?status=${filter}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
        return;
      }
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [filter, token, onLogout]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${id}/status`,
        { fulfillment_status: status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Marked ${STATUS_LABELS[status]}`);
      fetchOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status");
    }
  };

  const exportCsv = () => {
    const url = `${API}/admin/orders.csv?token=${encodeURIComponent(token)}${filter ? `&status=${filter}` : ""}`;
    window.open(url, "_blank");
  };

  const filtered = orders.filter((o) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      o.customer_name.toLowerCase().includes(q) ||
      o.customer_email.toLowerCase().includes(q) ||
      o.id.toLowerCase().includes(q) ||
      (o.customer_phone || "").includes(q)
    );
  });

  const counts = orders.reduce((acc, o) => {
    acc[o.fulfillment_status] = (acc[o.fulfillment_status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-dashboard">
      <header className="bg-[#2C4C3B] px-6 py-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.22em] text-[#E89149] uppercase">Admin</div>
          <div className="font-display text-xl text-white font-semibold">Thai Tasty Treats · Orders</div>
        </div>
        <div className="flex gap-3 items-center">
          <button
            onClick={exportCsv}
            data-testid="admin-export-csv"
            className="text-xs uppercase tracking-wider bg-[#E89149] hover:bg-[#D17F38] text-white px-3 py-2 rounded-md"
          >
            Export CSV
          </button>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="text-xs uppercase tracking-wider text-white/80 hover:text-white"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="px-6 py-5 flex flex-wrap items-center gap-2 border-b border-[#EFE7D6]">
        {["", "new", "in_progress", "fulfilled", "cancelled"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s || "all"}`}
            className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-md border transition ${
              filter === s
                ? "bg-[#2C4C3B] text-white border-[#2C4C3B]"
                : "bg-white text-[#2C4C3B] border-[#D9D2BD] hover:bg-[#F4ECD9]"
            }`}
          >
            {s ? STATUS_LABELS[s] : "All"}
            {s && counts[s] ? ` (${counts[s]})` : ""}
            {!s && orders.length ? ` (${orders.length})` : ""}
          </button>
        ))}
        <input
          type="text"
          placeholder="Search name, email, ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="admin-search-input"
          className="ml-auto px-3 py-1.5 border border-[#D9D2BD] rounded-md focus:outline-none focus:border-[#2C4C3B] bg-white text-sm w-72"
        />
        <button
          onClick={fetchOrders}
          data-testid="admin-refresh-btn"
          className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-md border border-[#D9D2BD] bg-white hover:bg-[#F4ECD9]"
        >
          Refresh
        </button>
      </div>

      <div className="px-6 py-4 overflow-x-auto">
        {loading ? (
          <div className="text-sm text-[#7A7A7A] py-12 text-center">Loading orders...</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-[#7A7A7A] py-12 text-center">No orders match.</div>
        ) : (
          <table className="w-full bg-white border border-[#EFE7D6] rounded-lg overflow-hidden">
            <thead className="bg-[#F4ECD9]">
              <tr className="text-[11px] uppercase tracking-wider text-[#2C4C3B]">
                <th className="px-4 py-3 text-left">ID</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Items</th>
                <th className="px-4 py-3 text-left">Method</th>
                <th className="px-4 py-3 text-left">Total</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <OrderRow key={o.id} order={o} onChangeStatus={updateStatus} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <LoginForm onLoggedIn={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
